import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { 
  ArrowLeft, ChevronRight, Play, Gamepad2, Sparkles, Box,
  MousePointer2, MessageSquare, Globe, Rocket, CheckCircle2, Loader2
} from 'lucide-react';
import useTranslatedItems from '../hooks/useTranslatedItems';
import { useI18n } from '../contexts/I18nContext';

const steps = [
  {
    id: 1,
    title: 'Create Your First Project',
    description: 'From the Dashboard, either start blank, describe a game and let AI generate it, or fork a starter Template. Free tier gives you 15 projects — more than most creators ever need.',
    icon: Gamepad2,
    tips: [
      'Prefer a fast start? Click Templates on the landing page or the "start from a template" link in the create dialog.',
      'Give your project a memorable name — this appears on the shareable card too.',
      'You can rename, edit or delete the project at any time.'
    ],
    video: null
  },
  {
    id: 2,
    title: 'Use the AI Game Generator (metered)',
    description: 'Describe your idea in plain English. Our AI (GPT-5 + Claude Sonnet) creates a complete playable scene in seconds. Free plan gets 5 generations/day, Pro 100/day, Studio effectively unlimited.',
    icon: Sparkles,
    tips: [
      'Be specific: "A platformer with 5 platforms, 3 enemies, 10 coins, ends with a portal"',
      'Set the mood: "spooky", "colorful synthwave", "low-poly noir"',
      'Describe gameplay: "enemies that patrol between two points", "coins that respawn"'
    ],
    example: '"Create a neon cyberpunk rooftop chase with laser walls, hoverbots and a data-core goal."'
  },
  {
    id: 3,
    title: 'Navigate the 3D Editor',
    description: 'BabylonJS-powered editor. Click objects to select, use gizmos to transform, tweak properties on the right panel.',
    icon: Box,
    tips: [
      'Left panel = scene outliner. Right panel = properties. Top toolbar = Move/Rotate/Scale.',
      'Use "Add object" for cubes/spheres/lights or drop in an AI-generated sprite (Assets tab).',
      'Undo/Redo is your friend — every change is captured.'
    ],
    controls: [
      { key: 'Left Click', action: 'Select object' },
      { key: 'Right Click + Drag', action: 'Rotate camera' },
      { key: 'Scroll', action: 'Zoom in/out' },
      { key: 'Middle Click + Drag', action: 'Pan camera' }
    ]
  },
  {
    id: 4,
    title: 'Chat with AI Copilot (metered)',
    description: 'Talk to your game in natural language. The Copilot understands the current scene and applies intelligent changes. Free tier gets 15 messages/day, Pro 300/day, Studio effectively unlimited.',
    icon: MessageSquare,
    tips: [
      '"Add 5 red enemies around the player"',
      '"Move the goal higher and add two moving platforms leading to it"',
      '"Change the sky to sunset and add a soft glow"',
      '"Give enemies a patrol behavior between the two pillars"'
    ]
  },
  {
    id: 5,
    title: 'Generate Assets (metered)',
    description: 'Need a sprite, texture or concept art? Open the Assets panel, pick sprite/texture/model, choose a style (pixel, realistic, toon, auto) and describe it. Free gets 5/day, Pro 100/day.',
    icon: Sparkles,
    tips: [
      'Sprites are 128×128 and isolated — perfect for characters and pickups.',
      'Textures are seamless-tileable — great for floors, walls and props.',
      '"model" returns concept art you can reference for future 3D imports.'
    ]
  },
  {
    id: 6,
    title: 'Test Your Game',
    description: 'Hit the Play button to test-run. Free tier plays are unlimited.',
    icon: Play,
    controls: [
      { key: 'W / ↑', action: 'Move forward' },
      { key: 'S / ↓', action: 'Move backward' },
      { key: 'A / ←', action: 'Move left' },
      { key: 'D / →', action: 'Move right' },
      { key: 'Space', action: 'Jump' },
      { key: 'Mouse', action: 'Look around' },
      { key: 'Esc', action: 'Exit play mode' }
    ]
  },
  {
    id: 7,
    title: 'Publish, Share & Get Featured',
    description: 'Click Publish in the editor header. Your game enters the Community Gallery. We auto-generate a rich OG share image via Gemini Nano Banana so Twitter/Discord posts show a stunning preview card.',
    icon: Globe,
    tips: [
      'Copy the share link from the Gallery card — it renders as a card in social apps.',
      'Games are ranked by (plays + 5× remixes + 2× likes) over 7 days for the Featured Game of the Week.',
      'Anyone can Remix your published game — you get credit in the remix count.',
      'You can Unpublish any time — the project stays in your dashboard.'
    ]
  },
  {
    id: 8,
    title: 'Deleted a project? Trash has your back',
    description: 'Deleting a project moves it to Trash for 30 days before permanent removal. Pro/Studio users can self-restore from the Trash icon on the Dashboard. Admin can recover any trashed project on request.',
    icon: Gamepad2,
    tips: [
      'Free users: contact support / admin to restore within 30 days.',
      'Pro/Studio: hit the trash icon on the Dashboard → Restore.',
      'Deleting your account (Settings → Danger Zone) is immediate and skips trash — per Google Play policy.'
    ]
  }
];

export default function Tutorial() {
  const navigate = useNavigate();
  const { language } = useI18n();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  // Flatten all translatable strings across all steps for a single batched request
  const { flatItems, spans } = useMemo(() => {
    const arr = [];
    const spans = [];
    steps.forEach(s => {
      const span = { title: -1, description: -1, tips: [], example: -1, controls: [] };
      span.title = arr.length; arr.push(s.title);
      span.description = arr.length; arr.push(s.description);
      (s.tips || []).forEach(tip => { span.tips.push(arr.length); arr.push(tip); });
      if (s.example) { span.example = arr.length; arr.push(s.example); }
      (s.controls || []).forEach(c => { span.controls.push(arr.length); arr.push(c.action); });
      spans.push(span);
    });
    return { flatItems: arr, spans };
  }, []);
  const [translated, translating] = useTranslatedItems('tutorial', flatItems);
  const localizedSteps = useMemo(() => {
    if (!translated || translated.length !== flatItems.length) return steps;
    return steps.map((s, i) => {
      const sp = spans[i];
      return {
        ...s,
        title: translated[sp.title] || s.title,
        description: translated[sp.description] || s.description,
        tips: (s.tips || []).map((t, k) => translated[sp.tips[k]] || t),
        example: s.example ? (translated[sp.example] || s.example) : undefined,
        controls: (s.controls || []).map((c, k) => ({ ...c, action: translated[sp.controls[k]] || c.action })),
      };
    });
  }, [translated, spans, flatItems]);

  const markComplete = (stepId) => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
    }
    if (currentStep < localizedSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const step = localizedSteps[currentStep];
  const Icon = step.icon;

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#1E1E24] bg-[#0A0A0C]/80 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <span className="text-zinc-500">|</span>
            <span className="text-white font-semibold">Getting Started Tutorial</span>
          </div>
          <div className="text-sm text-zinc-500 flex items-center gap-3">
            {translating && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#06B6D4]/10 border border-[#06B6D4]/30 text-[10px] text-[#06B6D4]" data-testid="tutorial-translating">
                <Loader2 className="w-3 h-3 animate-spin" /> {language.toUpperCase()}
              </span>
            )}
            Step {currentStep + 1} of {localizedSteps.length}
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-[#0A0A0C] border-b border-[#1E1E24] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-2">
          {localizedSteps.map((s, idx) => (
            <React.Fragment key={s.id}>
              <button
                onClick={() => setCurrentStep(idx)}
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all
                  ${idx === currentStep 
                    ? 'bg-[#06B6D4] text-black' 
                    : completedSteps.includes(s.id)
                      ? 'bg-emerald-500 text-white'
                      : 'bg-[#1E1E24] text-zinc-500 hover:bg-[#2E2E34]'
                  }
                `}
              >
                {completedSteps.includes(s.id) ? <CheckCircle2 className="w-5 h-5" /> : s.id}
              </button>
              {idx < localizedSteps.length - 1 && (
                <div className={`flex-1 h-1 rounded ${idx < currentStep ? 'bg-[#06B6D4]' : 'bg-[#1E1E24]'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 py-12 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Step Header */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 rounded-2xl bg-[#06B6D4]/20 flex items-center justify-center mx-auto mb-6">
              <Icon className="w-10 h-10 text-[#06B6D4]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-4">
              {step.title}
            </h1>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              {step.description}
            </p>
          </div>

          {/* Step Content */}
          <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-2xl p-8">
            {/* Tips */}
            {step.tips && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Tips</h3>
                <ul className="space-y-3">
                  {step.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-[#06B6D4] mt-0.5 shrink-0" />
                      <span className="text-zinc-300">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Example */}
            {step.example && (
              <div className="mb-8 p-4 bg-[#06B6D4]/10 border border-[#06B6D4]/30 rounded-lg">
                <h3 className="text-sm font-semibold text-[#06B6D4] mb-2">Example Prompt</h3>
                <p className="text-white font-mono">{step.example}</p>
              </div>
            )}

            {/* Controls */}
            {step.controls && (
              <div>
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Controls</h3>
                <div className="grid grid-cols-2 gap-3">
                  {step.controls.map((control, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-[#1E1E24] rounded-lg">
                      <kbd className="px-2 py-1 bg-[#2E2E34] rounded text-[#06B6D4] font-mono text-sm">
                        {control.key}
                      </kbd>
                      <span className="text-zinc-400 text-sm">{control.action}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <Button
              variant="ghost"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="text-zinc-400"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Previous
            </Button>

            {currentStep === localizedSteps.length - 1 ? (
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90"
              >
                Start Creating <Rocket className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => markComplete(step.id)}
                className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90"
              >
                Mark Complete & Continue <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
